import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Button, Form } from 'react-bootstrap';
import { useSelector } from "react-redux"
import { toast } from 'react-toastify';

export default function Config({ config, getConfig }) {

  const { user: authUser } = useSelector((x) => x.auth);
  const url = process.env.REACT_APP_URL;

  const [form, setForm] = useState({
    dayOfWeek: '',
    frequency: '',
    time: '',
    location: '',
    locationLink: '',
    title: '',
    description: '',
    itemsPerPage: []
  });
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [itemsPerPageInput, setItemsPerPageInput] = useState(form.itemsPerPage.join(','));

  useEffect(() => {
    setItemsPerPageInput(form.itemsPerPage.join(','));
  }, [form.itemsPerPage]);

  const handleItemsPerPageInputChange = (e) => {
    setItemsPerPageInput(e.target.value);
  };

  const processItemsPerPageInput = () => {
    const value = itemsPerPageInput
      .split(/[\s,;]+/) // Split by space, comma, or semicolon
      .map(v => Number(v.trim()))
      .filter(v => !isNaN(v) && v > 0);
    setForm(prev => ({
      ...prev,
      itemsPerPage: value
    }));
  };

  useEffect(() => {
    fetch(`${url}/meetingsConfig`, {
      headers: {
        'Authorization': `Bearer ${authUser.token}`,
      }
    })
      .then(res => {
        if (res.status === 404) {
          setNotFound(true);
          setLoading(false);
          return null;
        }
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => {
        if (data) {
          setForm({
            ...data,
            itemsPerPage: Array.isArray(data.itemsPerPage) ? data.itemsPerPage : []
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [url, authUser.token]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${url}/meetingsConfig`, {
        method: isCreating ? 'PUT' : 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        toast.success(
          isCreating ? 'Meetings configuration created!' : 'Meetings configuration updated!',
          { position: 'bottom-left' }
        );
        setIsCreating(false);
        setNotFound(false);
        getConfig();
      } else {
        const errorResponse = await response.json();
        toast.error(`Failed! ${errorResponse.error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error! ${error.message}`, { position: 'bottom-left' });
    }
  }, [form, url, isCreating, getConfig]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const handleCreateClick = () => {
    setIsCreating(true);
    setIsCollapsed(false);
    setForm({
      dayOfWeek: '',
      frequency: '',
      time: '',
      location: '',
      locationLink: '',
      title: '',
      description: '',
      itemsPerPage: []
    });
  };

  if (loading) return <div>Loading...</div>;

  if (notFound && !isCreating) {
    return (
      <Card id='meetings-config-card' className="mb-3 mt-3">
        <Card.Body>
          <Button variant="primary" onClick={handleCreateClick}>
            <i className='bi bi-plus-circle'></i> Create Meetings Configuration
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card id='meetings-config-card' className="mb-3 mt-3">
      <Form data-bs-theme={'dark'} onSubmit={handleSubmit}>
        <Card.Header onClick={toggleCollapse}>
          <Row className="header mb-2">
            <Col xs={11} className="title">
              <h5>Meetings Configuration</h5>
            </Col>
            <Col xs={1}>
              <i
                className={`bi ${isCollapsed ? 'bi-caret-down-fill' : 'bi-caret-up-fill'}`}
                style={{ cursor: 'pointer', paddingTop: '5px' }}
              ></i>
            </Col>
          </Row>
        </Card.Header>
        {!isCollapsed && (
          <>
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <p className="text-muted">
                    Configure the default settings for meetings. This will be used when creating new meetings, but all these settings can be overridden when creating or editing a specific meeting.
                  </p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Day of Week</Form.Label>
                    <Form.Select
                      name="dayOfWeek"
                      value={form.dayOfWeek || ''}
                      onChange={handleChange}
                    >
                      <option value="">Select day</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Frequency</Form.Label>
                    <Form.Select
                      name="frequency"
                      value={form.frequency || ''}
                      onChange={handleChange}
                    >
                      <option value="">Select frequency</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="bi-weekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half-yearly">Half-yearly</option>
                      <option value="yearly">Yearly</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Time</Form.Label>
                    <Form.Control
                      type="text"
                      name="time"
                      value={form.time || ''}
                      onChange={handleChange}
                      placeholder="e.g. 18:00"
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Default Location</Form.Label>
                    <Form.Control
                      type="text"
                      name="location"
                      value={form.location || ''}
                      onChange={handleChange}
                      placeholder="e.g. Main Hall"
                      maxLength={100}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Default Location Link</Form.Label>
                    <Form.Control
                      type="text"
                      name="locationLink"
                      value={form.locationLink || ''}
                      onChange={handleChange}
                      placeholder="e.g. https://maps.example.com"
                      maxLength={200}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={form.title || ''}
                      onChange={handleChange}
                      placeholder="Default Meeting Title"
                      maxLength={100}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={form.description || ''}
                      onChange={handleChange}
                      placeholder="Default Description"
                      maxLength={500}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Items Per Page (space, comma, or semicolon separated)</Form.Label>
                    <Form.Control
                      type="text"
                      name="itemsPerPage"
                      value={itemsPerPageInput}
                      onChange={handleItemsPerPageInputChange}
                      onBlur={processItemsPerPageInput}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          processItemsPerPageInput();
                        }
                      }}
                      placeholder="e.g. 10 20 50"
                    />
                    <p className="text-muted">The more items per page you want to display, the page performance will be affected.</p>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <Row>
                <Col xs={12}>
                  <Button variant="primary" type="submit" className="float-end">
                    <i className='bi bi-floppy'></i> Save
                  </Button>
                </Col>
              </Row>
            </Card.Footer>
          </>
        )}
      </Form>
    </Card>
  );
};